/**
 * useDifferentialStatus Hook
 * Polls the backend for LLM differential generation status
 * Automatically handles status transitions and completion
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { apiRequest } from '../Api';

export const useDifferentialStatus = (caseId, enabled = true) => {
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isFailed, setIsFailed] = useState(false);
  const pollIntervalRef = useRef(null);

  // Fetch current status
  const fetchStatus = useCallback(async () => {
    if (!caseId) return;

    try {
      setError(null);
      const response = await apiRequest(`/llm/differential-status/${caseId}`);
      setStatus(response);

      // Check if completed or failed
      if (response.status === 'Completed') {
        setIsCompleted(true);
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      } else if (response.status === 'Failed') {
        setIsFailed(true);
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      }
    } catch (err) {
      console.error('Error fetching differential status:', err);
      setError(err);
    }
  }, [caseId]);

  // Start polling
  const startPolling = useCallback(() => {
    if (!caseId || !enabled) return;

    // Initial fetch
    fetchStatus();

    // Set up interval (poll every 5 seconds)
    pollIntervalRef.current = setInterval(() => {
      fetchStatus();
    }, 5000);
  }, [caseId, enabled, fetchStatus]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  // Start polling when caseId or enabled changes
  useEffect(() => {
    if (enabled && caseId && !isCompleted && !isFailed) {
      startPolling();
    }

    return () => {
      stopPolling();
    };
  }, [caseId, enabled, isCompleted, isFailed, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    status,
    isLoading,
    error,
    isCompleted,
    isFailed,
    isPending: status?.status === 'Pending',
    isProcessing: status?.status === 'Processing',
    percentComplete: getProgressPercentage(status?.status),
    refetch: fetchStatus,
    startPolling,
    stopPolling,
  };
};

/**
 * Helper function to calculate progress percentage
 */
function getProgressPercentage(status) {
  switch (status) {
    case 'Pending':
      return 10;
    case 'Processing':
      return 50;
    case 'Completed':
      return 100;
    case 'Failed':
      return 0;
    default:
      return 0;
  }
}

export default useDifferentialStatus;
